import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	getAxiomData,
	getMonthlyEstimation,
	updateMonthlyData,
	type GetAxiomDataParams,
	type GetMonthlyEstimationParams,
	type UpdateMonthlyDataParams,
} from "../repositories/ForecastDataEntryRepository";

type UseMonthlyEstimationParams = Omit<GetMonthlyEstimationParams, "signal">;

export const useMonthlyEstimation = ({ year }: UseMonthlyEstimationParams) => {
	return useQuery({
		queryKey: ["forecast-data-entry", year],
		queryFn: ({ signal }) => getMonthlyEstimation({ year, signal }),
		enabled: !!year,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

interface UseUpdateMonthlyDataProps {
	onSuccess?: () => void;
	onError?: (err: Error) => void;
}

export const useUpdateMonthlyData = ({
	onSuccess,
	onError,
}: UseUpdateMonthlyDataProps = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (params: UpdateMonthlyDataParams) => updateMonthlyData(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["forecast-data-entry"] });
			onSuccess?.();
		},
		onError: (err: Error) => onError?.(err),
	});
	return {
		updateMonthlyData: mutation.mutate,
		isUpdating: mutation.isPending,
	};
};

export const useGetAxiomData = ({
	onSuccess,
	onError,
}: UseUpdateMonthlyDataProps = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (params: GetAxiomDataParams) => getAxiomData(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["forecast-data-entry"] });
			onSuccess?.();
		},
		onError: (err: Error) => onError?.(err),
	});
	return {
		getAxiomData: mutation.mutate,
		isFetchingAxiom: mutation.isPending,
	};
};
